import { c as _c } from "react/compiler-runtime";
import { DateCell } from "@components/common/date-cell";
import { DemoTag } from "@components/common/demo-tag";
import { ProjectManagerDisplay } from "@components/project/project-manager-display";
import { PROJECTS_TABLE_COLUMN_LABELS } from "@components/table/projects/projects-table-constants";
import styles from "@components/table/projects/projects-table.module.css";
import { UserAvatarWithMenu } from "@components/user-more-info-menu/user-avatar-with-menu";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { Box, Stack } from "@mui-wrapper";
import { generateGroupDetailsRoute, generateProjectDetailsRoute } from "@router/router-helper";
import { withEllipsis } from "@styles/common-styles";
import { getProjectGroupId, getProjectGroupName, getProjectSize, isProjectDemo } from "@utils/project-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { SHOW_ON_HOVER_CLASS } from "@utils/ui-utils";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { FontWeights, Text, Tooltip } from "@flat-ui";
import { FaroTableColumnVisibilityMenuIcon } from "@components/common/faro-table/faro-table-column-visibility-menu-icon";
import { useColumnPreferencesContext } from "@context-providers/table/column-preferences-provider";
import { TableHeaderWithDraggableIcon } from "@components/table/table-header-with-draggable-icon";
import { isProjectsTableColumnField } from "@components/table/projects/projects-table-utils";
import { ProjectTableThumbnail } from "@components/table/projects/project-table-thumbnail";
import { ProjectActions } from "@components/common/project-actions";
import { UiUtils } from "@stellar/utils";
import { FaroCoreApiUtils } from "@stellar/core-api-client";

/**
 * FLEX_WEIGHTS defines the relative flex-grow weights for resizable columns in the projects table.
 * Higher values make columns expand more when there is extra space.
 * Only columns listed here will have a custom flex value; others use the default.
 */
const FLEX_WEIGHTS = {
  name: 1
};

/**
 * FIXED_MAX_WIDTHS sets the maximum width (in pixels) for specific columns in the projects table.
 * This prevents columns from growing too wide, especially for columns with limited content.
 * Only columns listed here will have a maxWidth restriction.
 */
const FIXED_MAX_WIDTHS = {
  projectManager: 200,
  managerAvatar: 54,
  group: 200,
  created: 150,
  lastCaptured: 150,
  lastUpdated: 200,
  client: 200,
  size: 120,
  accessLevel: 120,
  tags: 120,
  options: 90
};

/**
 * FIXED_MIN_WIDTHS sets the minimum width (in pixels) for specific columns in the projects table.
 * This ensures important columns remain readable and do not shrink too much when space is limited.
 * Only columns listed here will have a minWidth restriction.
 */
const FIXED_MIN_WIDTHS = {
  name: 250,
  projectManager: 175,
  managerAvatar: 40,
  group: 150,
  created: 150,
  lastUpdated: 150,
  client: 150,
  lastCaptured: 150,
  size: 90,
  accessLevel: 120,
  tags: 90,
  options: 90
};
/**
 * @returns the columns definition to be used in the projects table
 */
export function useProjectsTableColumns(t0) {
  const $ = _c(35);
  const {
    companyId,
    contextType
  } = t0;
  const {
    canViewCompanyGroups
  } = useHasUserValidFeatureCompanyLevel();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    columnPreferences,
    visibleOrderedColumns
  } = useColumnPreferencesContext();
  let t1;
  bb0: {
    switch (contextType) {
      case "active projects":
        {
          t1 = "project-list-view";
          break bb0;
        }
      case "archived projects":
        {
          t1 = "project-list-view";
          break bb0;
        }
      case "group projects":
        {
          t1 = "group-project-list-view";
          break bb0;
        }
    }
    t1 = undefined;
  }
  const projectMenuContext = t1;
  let t2;
  if ($[0] !== companyId || $[1] !== contextType || $[2] !== trackEvent) {
    t2 = {
      field: "name",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.name,
      flex: FLEX_WEIGHTS.name,
      maxWidth: FIXED_MAX_WIDTHS.name,
      minWidth: FIXED_MIN_WIDTHS.name,
      sortable: true,
      disableReorder: true,
      renderCell: params => <Stack direction="row" sx={{
        ...withEllipsis,
        alignItems: "center",
        gap: "0.625rem"
      }}><ProjectTableThumbnail thumbnailUrl={params.row.entity.thumbnailUrl} projectId={params.row.entity.id} /><Tooltip component="div" title={<var>{params.row.entity.name}</var>} sx={{
          ...withEllipsis,
          fontWeight: FontWeights.Bold
        }}><Link to={generateProjectDetailsRoute(companyId, params.row.entity.id)} onClick={e => {
            trackEvent({
              name: ProjectEvents.openProjectDetails,
              props: {
                projectId: params.row.entity.id,
                eventLocation: contextType,
                viewMode: "list"
              }
            });
            e.stopPropagation();
          }} className={styles["removed-underline"]}><var>{params.row.entity.name}</var></Link></Tooltip></Stack>
    };
    $[0] = companyId;
    $[1] = contextType;
    $[2] = trackEvent;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      field: "managerAvatar",
      headerName: "",
      flex: FLEX_WEIGHTS.managerAvatar,
      maxWidth: FIXED_MAX_WIDTHS.managerAvatar,
      minWidth: FIXED_MIN_WIDTHS.managerAvatar,
      align: "right",
      renderCell: _temp,
      sortable: false
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== canViewCompanyGroups || $[6] !== companyId) {
    t4 = {
      field: "projectManager",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.projectManager,
      flex: FLEX_WEIGHTS.projectManager,
      maxWidth: FIXED_MAX_WIDTHS.projectManager,
      minWidth: FIXED_MIN_WIDTHS.projectManager,
      renderHeader: _temp2,
      renderCell: params_2 => <Box sx={{
        ...withEllipsis
      }}><ProjectManagerDisplay project={params_2.row.entity} companyId={companyId} canViewAllCompanyGroups={canViewCompanyGroups} /></Box>,
      sortable: false
    };
    $[5] = canViewCompanyGroups;
    $[6] = companyId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      field: "created",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.created,
      flex: FLEX_WEIGHTS.created,
      maxWidth: FIXED_MAX_WIDTHS.created,
      minWidth: FIXED_MIN_WIDTHS.created,
      sortable: true,
      renderHeader: _temp3,
      renderCell: _temp4
    };
    t6 = {
      field: "lastUpdated",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.lastUpdated,
      flex: FLEX_WEIGHTS.lastUpdated,
      maxWidth: FIXED_MAX_WIDTHS.lastUpdated,
      minWidth: FIXED_MIN_WIDTHS.lastUpdated,
      sortable: true,
      renderHeader: _temp5,
      renderCell: _temp6
    };
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[10] !== canViewCompanyGroups || $[11] !== companyId) {
    t7 = {
      field: "group",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.group,
      flex: FLEX_WEIGHTS.group,
      maxWidth: FIXED_MAX_WIDTHS.group,
      minWidth: FIXED_MIN_WIDTHS.group,
      renderHeader: _temp7,
      renderCell: params_8 => {
        const groupId = getProjectGroupId(params_8.row.entity);
        const tooltipContent = <Tooltip component="div" title={<var>{getProjectGroupName(params_8.row.entity)}</var>}><var>{getProjectGroupName(params_8.row.entity)}</var></Tooltip>;
        if (groupId && canViewCompanyGroups) {
          return <Link to={generateGroupDetailsRoute(companyId, groupId)} onClick={_temp8} className={styles["removed-underline"]}><Box sx={{
              ...withEllipsis
            }}>{tooltipContent}</Box></Link>;
        } else {
          return tooltipContent;
        }
      },
      sortable: false
    };
    $[10] = canViewCompanyGroups;
    $[11] = companyId;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      field: "accessLevel",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.accessLevel,
      flex: FLEX_WEIGHTS.accessLevel,
      maxWidth: FIXED_MAX_WIDTHS.accessLevel,
      minWidth: FIXED_MIN_WIDTHS.accessLevel,
      renderHeader: _temp9,
      renderCell: _temp0,
      sortable: false
    };
    t9 = {
      field: "tags",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.tags,
      flex: FLEX_WEIGHTS.tags,
      maxWidth: FIXED_MAX_WIDTHS.tags,
      minWidth: FIXED_MIN_WIDTHS.tags,
      renderCell: _temp1,
      sortable: false
    };
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  let t10;
  if ($[15] !== columnPreferences) {
    t10 = () => <FaroTableColumnVisibilityMenuIcon columnPreferences={columnPreferences} />;
    $[15] = columnPreferences;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== projectMenuContext) {
    t11 = params_12 => <Box component="div" sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "right",
      visibility: "hidden"
    }} className={SHOW_ON_HOVER_CLASS}><ProjectActions project={params_12.row.entity} contextType={projectMenuContext} /></Box>;
    $[17] = projectMenuContext;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10 || $[20] !== t11) {
    t12 = {
      field: "options",
      type: "actions",
      align: "right",
      flex: FLEX_WEIGHTS.options,
      maxWidth: FIXED_MAX_WIDTHS.options,
      minWidth: FIXED_MIN_WIDTHS.options,
      disableReorder: true,
      renderHeader: t10,
      renderCell: t11,
      sortable: false
    };
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  let t14;
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      field: "client",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.client,
      flex: FLEX_WEIGHTS.client,
      maxWidth: FIXED_MAX_WIDTHS.client,
      minWidth: FIXED_MIN_WIDTHS.client,
      renderHeader: _temp10,
      renderCell: _temp11,
      sortable: false
    };
    t14 = {
      field: "size",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.size,
      flex: FLEX_WEIGHTS.size,
      maxWidth: FIXED_MAX_WIDTHS.size,
      minWidth: FIXED_MIN_WIDTHS.size,
      sortable: true,
      renderHeader: _temp12,
      renderCell: _temp13
    };
    t15 = {
      field: "lastCaptured",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.lastCaptured,
      flex: FLEX_WEIGHTS.lastCaptured,
      maxWidth: FIXED_MAX_WIDTHS.lastCaptured,
      minWidth: FIXED_MIN_WIDTHS.lastCaptured,
      sortable: true,
      renderHeader: _temp14,
      renderCell: _temp15
    };
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
  } else {
    t13 = $[22];
    t14 = $[23];
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t12 || $[26] !== t2 || $[27] !== t4 || $[28] !== t7) {
    t16 = {
      name: t2,
      managerAvatar: t3,
      projectManager: t4,
      created: t5,
      lastUpdated: t6,
      group: t7,
      accessLevel: t8,
      tags: t9,
      options: t12,
      client: t13,
      size: t14,
      lastCaptured: t15
    };
    $[25] = t12;
    $[26] = t2;
    $[27] = t4;
    $[28] = t7;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const columnsMap = t16;
  let t17;
  if ($[30] !== columnsMap || $[31] !== visibleOrderedColumns) {
    let t18;
    if ($[33] !== columnsMap) {
      t18 = header => columnsMap[header];
      $[33] = columnsMap;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    t17 = visibleOrderedColumns.filter(isProjectsTableColumnField).map(t18);
    $[30] = columnsMap;
    $[31] = visibleOrderedColumns;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const orderedColumns = t17;
  return orderedColumns;
}
function _temp15(params_18) {
  const lastCapturedAt = params_18.row.entity.lastCapturedAt;
  if (!lastCapturedAt) {
    return UiUtils.DASH;
  }
  return <DateCell date={lastCapturedAt} />;
}
function _temp14(params_17) {
  return <TableHeaderWithDraggableIcon title={params_17.colDef.headerName ?? ""} />;
}
function _temp13(params_16) {
  return <Text variant="bodyS" component="var">{getProjectSize(params_16.row.entity)}</Text>;
}
function _temp12(params_15) {
  return <TableHeaderWithDraggableIcon title={params_15.colDef.headerName ?? ""} />;
}
function _temp11(params_14) {
  return <Text variant="bodyS"><var>{params_14.row.entity.clientName || UiUtils.DASH}</var></Text>;
}
function _temp10(params_13) {
  return <TableHeaderWithDraggableIcon title={params_13.colDef.headerName ?? ""} />;
}
function _temp1(params_11) {
  return isProjectDemo(params_11.row.entity) ? <DemoTag /> : "";
}
function _temp0(params_10) {
  const accessLevel = params_10.row.entity.accessLevel;
  return FaroCoreApiUtils.Project.AccessLevelDisplayNames[accessLevel];
}
function _temp9(params_9) {
  return <TableHeaderWithDraggableIcon title={params_9.colDef.headerName ?? ""} />;
}
function _temp8(e_0) {
  e_0.stopPropagation();
}
function _temp7(params_7) {
  return <TableHeaderWithDraggableIcon title={params_7.colDef.headerName ?? ""} />;
}
function _temp6(params_6) {
  return <DateCell date={params_6.row.entity.modifiedAt} />;
}
function _temp5(params_5) {
  return <TableHeaderWithDraggableIcon title={params_5.colDef.headerName ?? ""} />;
}
function _temp4(params_4) {
  return <DateCell date={params_4.row.entity.createdAt} />;
}
function _temp3(params_3) {
  return <TableHeaderWithDraggableIcon title={params_3.colDef.headerName ?? ""} />;
}
function _temp2(params_1) {
  return <TableHeaderWithDraggableIcon title={params_1.colDef.headerName ?? ""} />;
}
function _temp(params_0) {
  return <Box component="div" sx={{
    display: "flex",
    alignItems: "center",
    pr: "0px"
  }}><UserAvatarWithMenu member={params_0.row.entity.managers?.projectManager} /></Box>;
}